import { useState, useCallback, useContext, useEffect, lazy, Suspense } from 'react';
import {
  rankWith,
  ControlProps,
  isControl,
  update,
  resolveSchema,
} from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Autocomplete, IconButton, TextField } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { apiClient } from '@/lib/apiClient';
import { entityDetailPath, canonicalModuleName } from '@/lib/pageEntityContext';
import { useHasPermission } from '@/components/PermissionGate';
import { InsideReferencePeekContext } from '@/components/InsideReferencePeekContext';
import { EntityLookup, EntityLookupOption } from '../EntityLookup';
import { FieldLabel } from '../FieldLabel';
import { recordRecentLookup, resolveLookupFilters, type LookupFilterConfig } from '@/lib/lookupOptions';
import { EntitySearchModal } from '../EntitySearchModal';
import { REF_PEEK_CLASS, revealRefPeekSx } from '@/components/referencePeekReveal';

/** Lazy: the peek panel pulls in JsonForms + all renderers; only pay on first use. */
const ReferencePeekPanelLazy = lazy(() => import('@/components/ReferencePeekPanel'));

/**
 * AutoFill mapping configuration
 */
interface AutoFillMapping {
  target: string;   // Target field path in form data (e.g., "primaryQuantity.uomCode")
  source: string;   // Source field path in selected entity (e.g., "unitOfMeasure.code")
  // This mapping's own overwrite, over the config's: a referenced value a visibility rule reads always follows the pick.
  overwriteExisting?: boolean;
}

interface AutoFillConfig {
  mappings: AutoFillMapping[];
  overwriteExisting?: boolean;  // Default: false - only fill empty fields
}

/**
 * Get a nested value from an object using dot notation path
 * Handles both camelCase and PascalCase property names
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;

  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;

    // Try camelCase first, then PascalCase
    const camelValue = current[part];
    const pascalPart = part.charAt(0).toUpperCase() + part.slice(1);
    const pascalValue = current[pascalPart];

    current = camelValue !== undefined ? camelValue : pascalValue;
  }

  return current;
}


/**
 * Tester function to determine if this renderer should be used
 */
export const lookupRendererTester = rankWith(
  10, // High priority to override default renderers
  (uischema, schema, context) => {
    if (!isControl(uischema) || !schema) {
      return false;
    }

    // JSONForms passes the ROOT schema here, not the property subschema — so
    // `schema.format`/`schema['x-lookup']` on it are undefined. Resolve the control's
    // scope against the root schema to inspect the actual field. (Falls back to the
    // raw schema for safety.)
    const scope = (uischema as any).scope as string | undefined;
    const rootSchema = (context as any)?.rootSchema ?? schema;
    let fieldSchema: any = schema;
    if (scope) {
      try {
        fieldSchema = resolveSchema(rootSchema, scope, rootSchema) ?? schema;
      } catch {
        fieldSchema = schema;
      }
    }

    const fmt = fieldSchema?.format;
    const hasLookupFormat = fmt === 'entity-lookup' || fmt === 'api-lookup' || fmt === 'code-lookup' || fmt === 'user-lookup';
    const hasXLookup = !!fieldSchema?.['x-lookup'];
    const hasLookupOptions = !!(uischema.options as any)?.lookup;

    return hasLookupFormat || hasXLookup || hasLookupOptions;
  }
);

/**
 * While a lookup waits for a required filter source it cannot be filled, so "required" is noise and
 * would hide the hint naming what to fill in first; the save still validates it.
 */
function waitingForSourceProps(waiting: boolean, errors: string, waitText: string) {
  return waiting ? { error: undefined, hint: waitText } : { error: errors, hint: undefined };
}

/**
 * Lookup renderer component
 */
/**
 * Whether a picked record's source value fills its autoFill target. A value the record does not carry fills
 * nothing; a null fills nothing either — unless the mapping snapshots the selection (`overwriteExisting`), where
 * keeping the previous pick's value would describe a record no longer picked (Computed-Fields.md §3).
 */
function autoFillCarries(sourceValue: unknown, overwriteExisting: boolean): boolean {
  if (sourceValue === undefined) return false;
  return sourceValue !== null || overwriteExisting;
}

export const LookupRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, description, required, errors, schema, uischema, visible, enabled } = props;
  const isReadOnly = !enabled || uischema.options?.readOnly === true;
  const [modalOpen, setModalOpen] = useState(false);
  const [peekOpen, setPeekOpen] = useState(false);
  // Stays true after the first open so the drawer remains mounted and its
  // slide-out animation can run on close.
  const [peekMounted, setPeekMounted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get access to the full form context for autoFill functionality
  const ctx = useJsonForms();
  const formData = ctx.core?.data || {};
  const { t } = useTranslation();

  // Extract lookup configuration from schema x-lookup or uischema options
  const schemaLookupConfig = (schema as any)['x-lookup'] || {};
  const uiLookupConfig = uischema.options?.lookup || {};

  // Merge configurations (uischema takes precedence)
  const lookupConfig = { ...schemaLookupConfig, ...uiLookupConfig };

  const {
    entity,
    displayField,
    displayPrefixField,
    searchFields = [],
    additionalFields = [],
    columns = [],
    minSearchLength = 2,
    autoFill,
    codeField,
    recentlyUsedSlot,
  } = lookupConfig as {
    entity: string;
    displayField: string;
    displayPrefixField?: string;
    searchFields: string[];
    additionalFields: string[];
    columns?: string[];  // lookup.columns: shown as columns of their own in the search dialog
    minSearchLength: number;
    autoFill?: AutoFillConfig;
    codeField?: string;
    recentlyUsedSlot?: string;  // Generated per field unless the DSL opts out (lookup spec §4)
  };

  // Get module from lookup config, uischema options, or fall back to URL path
  // Cross-module lookups (e.g., Product -> UnitOfMeasure) require explicit module in DSL
  const module = lookupConfig.module ||
                 uischema.options?.lookup?.module ||
                 window.location.pathname.split('/')[1] ||
                 'sales';

  // Opening the referenced record — read-only link or peek panel — is offered
  // only when the user may read that entity; the server enforces it regardless.
  // Unconditional hook call: entity is undefined in user/api lookup modes and
  // the permission check simply comes back false.
  //
  // Suppressed inside the peek panel (no nested peeks) and for code-valued
  // lookups (codeField): their stored value is a code like "ZA", not an id,
  // so there is no record address to link to.
  const insidePeekPanel = useContext(InsideReferencePeekContext);
  const canReadReference =
    useHasPermission(`${canonicalModuleName(module)}.${entity ?? ''}.Read`)
    && !insidePeekPanel
    && !codeField;

  // Handle value change
  const onChange = (value: string | null) => {
    handleChange(path, value);
  };

  // Handle autoFill when an entity is selected
  const handleEntitySelect = useCallback((selectedEntity: EntityLookupOption | null) => {
    if (!selectedEntity || !autoFill?.mappings || autoFill.mappings.length === 0) {
      return;
    }

    // Process each autoFill mapping
    for (const mapping of autoFill.mappings) {
      const { target, source } = mapping;
      const overwriteExisting = mapping.overwriteExisting ?? autoFill.overwriteExisting ?? false;

      // Get the source value from the selected entity
      const sourceValue = getNestedValue(selectedEntity, source);

      if (!autoFillCarries(sourceValue, overwriteExisting)) {
        console.debug(`[AutoFill] Source field '${source}' has no value in the selected entity`, selectedEntity);
        continue;
      }

      // Check if target already has a value (if overwriteExisting is false)
      if (!overwriteExisting) {
        const existingValue = getNestedValue(formData, target);
        if (existingValue !== undefined && existingValue !== null && existingValue !== '') {
          // Skip - don't overwrite existing value
          console.debug(`[AutoFill] Skipping '${target}' - already has value: ${existingValue}`);
          continue;
        }
      }

      // Update the target field using JSON Forms dispatch
      // The 'update' action allows updating arbitrary paths in the form data
      console.debug(`[AutoFill] Setting '${target}' to '${sourceValue}'`);
      ctx.dispatch?.(update(target, () => sourceValue));
    }
  }, [autoFill, formData, ctx]);

  // Handle modal select
  const handleModalSelect = (item: any) => {
    // A codeLookup stores the code, not the id — same value the autocomplete stores.
    const picked = codeField ? item[codeField] : item.id;
    onChange(picked);
    // A pick in the search modal is a selection like one in the dropdown.
    if (recentlyUsedSlot) recordRecentLookup(recentlyUsedSlot, picked);
    handleEntitySelect(item);
    setModalOpen(false);
  };

  // ── Dependent-lookup filters (lookup spec §5) ──
  const declaredFilters: LookupFilterConfig[] = lookupConfig.filters || [];
  const hasLookupFilters = declaredFilters.length > 0;
  const lookupFilters = resolveLookupFilters(declaredFilters, formData, path);
  const waitingForSource = lookupFilters.missingRequired.length > 0;
  const filterWaitText = t('forms.selectFieldFirst', {
    field: lookupFilters.missingRequired
      .map(name => (ctx.core?.schema as any)?.properties?.[name]?.title || name)
      .join(', '),
    defaultValue: 'Select {{field}} first',
  });

  // ── API Lookup Mode (non-entity data source) ──
  const isApiMode = lookupConfig.apiMode === true;
  const [apiOptions, setApiOptions] = useState<any[]>([]);
  const [apiLoading, setApiLoading] = useState(false);

  // Dependent lookup: the DSL may name sibling fields whose values become query parameters
  // (e.g. an approval policy's trigger list depends on its entity type). Until every one of
  // them has a value there is nothing to ask for, so the control stays disabled rather than
  // showing an empty list with no explanation.
  const paramsFromFields: Record<string, string> = lookupConfig.paramsFromFields || {};
  const dependencyNames = Object.keys(paramsFromFields);
  const sourceParams: Record<string, any> = {};
  const missingSources: string[] = [];
  for (const paramName of dependencyNames) {
    const sourceField = paramsFromFields[paramName];
    const sourceValue = getNestedValue(formData, sourceField);
    if (sourceValue === undefined || sourceValue === null || sourceValue === '') {
      missingSources.push(sourceField);
    } else {
      sourceParams[paramName] = sourceValue;
    }
  }
  const hasAllSources = missingSources.length === 0;
  // Serialized so the effect refetches when a source changes, not on every render.
  const sourceParamsKey = JSON.stringify(sourceParams);
  // Name the field they have to fill in, using its form label rather than its property name.
  const missingSourceLabels = missingSources.map(fieldName => {
    const rootProps = (ctx.core?.schema as any)?.properties || {};
    return rootProps[fieldName]?.title || fieldName;
  });
  const dependencyHelperText = t('forms.selectFieldFirst', {
    field: missingSourceLabels.join(', '),
    defaultValue: 'Select {{field}} first',
  });

  useEffect(() => {
    if (!isApiMode || !lookupConfig.endpoint) return;
    if (!hasAllSources) {
      setApiOptions([]);
      return;
    }
    setApiLoading(true);
    apiClient.get(lookupConfig.endpoint, { params: JSON.parse(sourceParamsKey) })
      .then(res => setApiOptions(res.data || []))
      .catch(() => setApiOptions([]))
      .finally(() => setApiLoading(false));
  }, [isApiMode, lookupConfig.endpoint, hasAllSources, sourceParamsKey]);

  // ── User Lookup Mode (ABP IdentityUser search) ──
  const isUserMode = (schema as any).format === 'user-lookup' || lookupConfig.userLookup === true;
  const userEndpoint = lookupConfig.endpoint || '/api/identity/user-picker/search';
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [userInput, setUserInput] = useState('');
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    if (!isUserMode) return;
    setUserLoading(true);
    apiClient.get(userEndpoint, { params: { filter: userInput || '', maxResultCount: 25 } })
      .then(res => setUserOptions(res.data?.items ?? res.data ?? []))
      .catch(() => setUserOptions([]))
      .finally(() => setUserLoading(false));
  }, [isUserMode, userEndpoint, userInput]);

  if (isUserMode) {
    if (visible === false) return null;
    const userLabel = (u: any): string => {
      if (!u) return '';
      const full = [u.name, u.surname].filter(Boolean).join(' ');
      return full || u.userName || u.id || '';
    };
    // Current selection: prefer a searched option, else synthesize from the
    // server-resolved {Field}Name on the form data so the existing value shows.
    // Resolve the name as a SIBLING of the field (relative to its parent), so a User
    // field nested in a child collection reads the right object, not the root.
    const segs = (path || '').split('.');
    const fieldSeg = segs.pop() || '';
    const nameKey = `${fieldSeg.replace(/Id$/, '')}Name`;
    const parentPath = segs.join('.');
    const resolvedName = parentPath ? getNestedValue(formData, `${parentPath}.${nameKey}`) : formData[nameKey];
    const current = data
      ? (userOptions.find((o: any) => o.id === data) || { id: data, userName: resolvedName })
      : null;
    const options = data && current && !userOptions.some((o: any) => o.id === data)
      ? [current, ...userOptions]
      : userOptions;
    return (
      <Autocomplete
        options={options}
        getOptionLabel={userLabel}
        value={current}
        loading={userLoading}
        disabled={isReadOnly}
        onInputChange={(_e, val) => setUserInput(val)}
        onChange={(_e, newValue: any) => handleChange(path, newValue ? newValue.id : null)}
        isOptionEqualToValue={(opt: any, val: any) => opt?.id === val?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={<FieldLabel label={label ?? ''} description={description} />}
            required={required}
            error={!!errors}
            helperText={errors}
            variant="standard"
            size="small"
          />
        )}
        size="small"
      />
    );
  }

  if (isApiMode) {
    if (visible === false) return null;

    const valueField = lookupConfig.valueField || 'id';
    const dispField = lookupConfig.displayField || 'name';

    // A stored value the catalog does not offer must survive. It happens legitimately — a value
    // configured before the catalog existed, or one an external producer sends — and without
    // this the field renders empty and the next save silently wipes a working configuration.
    const known = apiOptions.find((o: any) => o[valueField] === data) || null;
    const selectedOption = known
      ?? (data ? { [valueField]: data, [dispField]: String(data) } : null);
    const options = known || !data ? apiOptions : [selectedOption, ...apiOptions];

    return (
      <Autocomplete
        options={options}
        getOptionLabel={(opt: any) => opt[dispField] || ''}
        value={selectedOption}
        loading={apiLoading}
        disabled={isReadOnly || !hasAllSources}
        onChange={(_e, newValue) => {
          handleChange(path, newValue ? newValue[valueField] : null);
        }}
        isOptionEqualToValue={(opt: any, val: any) => opt[valueField] === val[valueField]}
        renderInput={(params) => (
          <TextField
            {...params}
            label={<FieldLabel label={label ?? ''} description={description} />}
            required={required}
            error={!!errors}
            helperText={errors || (hasAllSources ? undefined : dependencyHelperText)}
            variant="standard"
            size="small"
          />
        )}
        size="small"
      />
    );
  }

  if (!entity || !displayField || searchFields.length === 0) {
    return (
      <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
        <strong>Lookup configuration error</strong>
        <br />
        Entity: {entity || 'missing'}
        <br />
        DisplayField: {displayField || 'missing'}
        <br />
        SearchFields: {searchFields.length === 0 ? 'missing' : searchFields.join(', ')}
        <br />
        <pre style={{ fontSize: '10px', marginTop: '5px' }}>
          {JSON.stringify({ schema: (schema as any)['x-lookup'], uiOptions: uischema.options }, null, 2)}
        </pre>
      </div>
    );
  }

  // Respect JSONForms visibility rules (e.g., visibleWhen in DSL)
  // Must be after all hooks to avoid "rendered more hooks" error.
  if (visible === false) return null;

  // Read-only mode: show display name as a link instead of editable lookup.
  // Click navigates in-app (Cmd/Ctrl+click in a new tab); the hover eye icon
  // opens the peek panel. Without read permission it is plain text.
  if (isReadOnly && data) {
    const displayValue = formData[`${path.split('.').pop()?.replace(/Id$/, '')}Name`]
      || formData[`${entity?.toLowerCase()}Name`]
      || data;
    const linkPath = canReadReference ? entityDetailPath(module, entity, data) : null;
    return (
      <>
        <TextField
          label={<FieldLabel label={label ?? ''} description={description} />}
          value={displayValue}
          variant="standard"
          size="small"
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: linkPath ? (
                <IconButton
                  className={REF_PEEK_CLASS}
                  size="small"
                  title={t('lookup.quickView', { defaultValue: 'Quick view' })}
                  aria-label={t('lookup.quickView', { defaultValue: 'Quick view' })}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.metaKey || e.ctrlKey) {
                      window.open(linkPath, '_blank');
                      return;
                    }
                    setPeekMounted(true);
                    setPeekOpen(true);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              ) : undefined,
            },
          }}
          onClick={
            linkPath
              ? (e) => {
                  if (e.metaKey || e.ctrlKey) {
                    window.open(linkPath, '_blank');
                    return;
                  }
                  navigate(linkPath, { state: { returnTo: location.pathname + location.search } });
                }
              : undefined
          }
          sx={
            linkPath
              ? {
                  ...revealRefPeekSx(peekOpen),
                  '& input': { cursor: 'pointer', color: 'primary.main' },
                }
              : undefined
          }
        />
        {peekMounted && (
          <Suspense fallback={null}>
            <ReferencePeekPanelLazy
              open={peekOpen}
              onClose={() => setPeekOpen(false)}
              module={module}
              entity={entity}
              entityId={data}
              recordLabel={String(displayValue)}
            />
          </Suspense>
        )}
      </>
    );
  }

  return (
    <>
      <EntityLookup
        entity={entity}
        module={module}
        value={data || null}
        onChange={onChange}
        onEntitySelect={handleEntitySelect}
        displayField={displayField}
        displayPrefixField={displayPrefixField}
        additionalFields={additionalFields}
        label={label}
        description={description}
        required={required}
        {...waitingForSourceProps(waitingForSource, errors, filterWaitText)}
        minSearchLength={minSearchLength}
        filters={lookupFilters.values}
        filterSourceMissing={waitingForSource}
        // Read-only and empty reaches here (the read-only view above needs a value to show).
        disabled={isReadOnly || waitingForSource}
        // The modal search loads the whole entity and cannot apply dependent filters, so it is
        // not offered where they are declared.
        onOpenModal={hasLookupFilters ? undefined : () => setModalOpen(true)}
        recentlyUsedSlot={recentlyUsedSlot}
        {...(codeField ? { valueField: codeField } : {})}
      />
      
      <EntitySearchModal
        entity={entity}
        module={module}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleModalSelect}
        searchFields={searchFields}
        displayField={displayField}
        additionalFields={additionalFields}
        columns={columns}
      />
    </>
  );
};

/**
 * Connected lookup renderer with JSON Forms props
 */
export const LookupRenderer = withJsonFormsControlProps(LookupRendererComponent);

/**
 * Renderer entry for registration
 */
export const lookupRendererEntry = {
  tester: lookupRendererTester,
  renderer: LookupRenderer,
};

